import { mediaUrl } from "./media-url";

export const POKER_CARD_MOVEMENT_SOUNDS = [
  mediaUrl("/assets/audio/poker-cards/card-draw-01.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-02.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-03.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-04.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-05.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-06.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-07.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-draw-08.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-slide-01.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-slide-02.mp3"),
  mediaUrl("/assets/audio/poker-cards/card-slide-03.mp3"),
] as const;

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;
type PokerCardAudioEngine = {
  context: AudioContext;
  output: DynamicsCompressorNode;
  buffers: Map<string, AudioBuffer>;
  lastClip: number;
};

let engine: PokerCardAudioEngine | undefined;
let preloadJob: Promise<void> | undefined;

export function selectPokerCardMovementIndex(previous: number, random = Math.random) {
  const count = POKER_CARD_MOVEMENT_SOUNDS.length;
  const available = previous >= 0 && previous < count ? count - 1 : count;
  const sample = Math.max(0, Math.min(.999999999, random()));
  const selected = Math.floor(sample * available);
  return available === count || selected < previous ? selected : selected + 1;
}

function createPokerCardAudioEngine() {
  if (engine) return engine;
  const constructor = (typeof AudioContext === "undefined" ? (globalThis as typeof globalThis & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext : AudioContext) as AudioContextConstructor | undefined;
  if (!constructor) return undefined;
  const context = new constructor({ latencyHint: "interactive" });
  const output = context.createDynamicsCompressor();
  output.threshold.value = -22;
  output.knee.value = 16;
  output.ratio.value = 7;
  output.attack.value = .002;
  output.release.value = .12;
  output.connect(context.destination);
  engine = { context, output, buffers: new Map(), lastClip: -1 };
  return engine;
}

/** Fetch and decode the complete card-physics bank before the first movement. */
export function preloadPokerCardMovementAudio() {
  if (preloadJob) return preloadJob;
  const audioEngine = createPokerCardAudioEngine();
  if (!audioEngine) return Promise.resolve();
  preloadJob = Promise.all(POKER_CARD_MOVEMENT_SOUNDS.map(async (source) => {
    const response = await fetch(source, { cache: "force-cache" });
    if (!response.ok) throw new Error(`Could not preload ${source}`);
    audioEngine.buffers.set(source, await audioEngine.context.decodeAudioData(await response.arrayBuffer()));
  })).then(() => undefined);
  return preloadJob;
}

/** Resume from the Player Display arm or player-remote join gesture. */
export async function unlockPokerCardMovementAudio() {
  const audioEngine = createPokerCardAudioEngine();
  if (audioEngine?.context.state === "suspended") await audioEngine.context.resume().catch(() => undefined);
}

export type PokerCardMovementPlayback = { source: string; played: boolean };

/** A new buffer voice per card keeps rapid deals and multi-card folds audible. */
export function playPokerCardMovementSound(delayMs = 0, random = Math.random): PokerCardMovementPlayback {
  const audioEngine = createPokerCardAudioEngine();
  const clipIndex = selectPokerCardMovementIndex(audioEngine?.lastClip ?? -1, random);
  const source = POKER_CARD_MOVEMENT_SOUNDS[clipIndex];
  if (!audioEngine) return { source, played: false };
  audioEngine.lastClip = clipIndex;
  const buffer = audioEngine.buffers.get(source);
  if (!buffer || audioEngine.context.state === "closed") return { source, played: false };
  const voice = audioEngine.context.createBufferSource();
  const gain = audioEngine.context.createGain();
  voice.buffer = buffer;
  voice.playbackRate.value = .97 + random() * .06;
  gain.gain.value = .34;
  voice.connect(gain);
  gain.connect(audioEngine.output);
  voice.start(audioEngine.context.currentTime + Math.max(0, delayMs) / 1_000);
  return { source, played: true };
}
